import { DeepKeys } from 'utils/type-tools';

const customization = {
  /**
   * Exposed project specific variables like logos, email addresses, etc.
   */
  variables: {
    pdf: {
      logo: '/@danfloss/pdf-default-logo.png',
    },
    bottomLogo: '/@danfloss/bottom_logo.png',
    contactEmail: process.env.REACT_APP_CONTACT_EMAIL,
    contactEmailTitle: 'Custom Assembly Configuration',
    companyName: '',
    showCartButton: '',
    showHeader: 'true',
    fitting1NotSelectedImage: '/@danfloss/fitting.png',
    fitting2NotSelectedImage: '/@danfloss/fitting.png',
    fittingNoImage: '/@danfloss/no-image-square.png',
    hoseNotSelectedImage: '/@danfloss/hose.png',
    hoseNoImage: '/@danfloss/no-image-21.png',
  },
  /**
   * CSS variable overrides
   * @see /src/styles/css-variables.scss
   */
  css: {
    'font-family-primary': 'Roboto, sans-serif',
    'font-family-secondary': 'Roboto, sans-serif',
    // Primary color
    'color-accent': '#ED071B',
    'color-primary': 'var(--color-accent)',
    'color-primary-hover': 'color-mix(in srgb, var(--color-accent), black 10%)',
    'color-primary-hover-shadow': 'color-mix(in srgb, var(--color-accent), transparent 50%)',
    'color-primary-active': 'color-mix(in srgb, var(--color-accent), black 20%)',
    'color-primary-disabled': 'color-mix(in srgb, var(--color-accent), white 20%)',
    'color-primary-disabled-font': 'color-mix(in srgb, var(--color-accent), white 90%)',
    // Secondary
    'color-secondary': '#BFBEBE',
    'color-secondary-hover': 'color-mix(in srgb, var(--color-secondary), black 10%)',
    'color-secondary-hover-shadow': 'color-mix(in srgb, var(--color-secondary), transparent 50%)',
    'color-secondary-active': 'color-mix(in srgb, var(--color-secondary), black 20%)',
    'color-secondary-disabled': 'color-mix(in srgb, var(--color-secondary), white 20%)',
    'color-secondary-disabled-font': 'color-mix(in srgb, var(--color-secondary), var(--color-primary) 50%)',
    // Background of body
    'color-bgc': 'white',
    // Checkboxes
    'color-checkbox-select': 'var(--color-primary)',
    // Other stuff
    'button-border-radius': '7px',
    'button-secondary-disabled-text': 'white',
    'button-secondary-text': 'white',
    // steps on top
    'progress-bar-background-color': '#F5F5F7',
    'progress-bar-border-color': 'transparent',
    'progress-bar-box-shadow': '0px 2px 12px rgba(0, 0, 0, 0.1)',
    'color-step-line': 'color-mix(in srgb, var(--color-primary), transparent 60%)',
    'color-step-line-mobile-active': 'var(--color-primary)',
    'color-step-line-mobile-complete': 'var(--color-primary)',
  },
  texts: {
    intro: {
      part1: 'Hose Assembly',
      part2: 'Pro',
      // eslint-disable-next-line max-len
      description: 'Eliminate the uncertainty in constructing a hose assembly with our interactive tool. Effortlessly select hoses and fittings using this user-friendly tool, which offers options based on your inputs, including suitable fittings, working pressure, size, and more.',
      by: 'by',
      logo1Url: '/@danfloss/logo1.svg',
      logo2Url: '/@danfloss/logo2.png',
    },
    step1: {
      title: 'Choose Your Hose',
      tabName: 'Select Hose',
      description: 'Please use the below drop-down options to select your hose specifications.',
    },
    step2: {
      title: 'Select Fittings',
      tabName: 'Select Fittings',
      description: 'Please use the below drop downs to select the end style and size you need.',
    },
    step3: {
      title: 'Overall Assembly Length',
      tabName: 'Assembly Length',
      description: 'Please enter the required finished assembly length in inches or feet. See below images for measurement guide.',
    },
    step4: {
      title: 'Angle of Rotation',
      tabName: 'Angle of Rotation',
      description: `Please enter the angle of rotation between fitting&nbsp;1 and fitting&nbsp;2 using the image below as 
a&nbsp;guide.<br/> Fitting 1 will be 0 degrees.`,
    },
    step5: {
      title: 'Accessories/Options',
      tabName: 'Accessories',
      description: 'Please select any optional accessories from the list of available.',
      customerPartNumberPlaceholder: 'Enter Customer Part Number',
    },
    step6: {
      title: 'Confirm Your Assembly',
      tabName: 'Confirm Assembly',
      // eslint-disable-next-line max-len
      description: 'Please verify component selections, specifications, and assembly length for your application. Custom assemblies are non-returnable and non-refundable. To modify, click the item below.',
    },
    pdf: {
      title: 'Your Assembly',
      // eslint-disable-next-line max-len
      tos: 'Please verify component selections, specifications, and assembly length for your application. Custom assemblies are non-returnable and non-refundable.',
      hoseSelection: 'Hose Selection',
      fitting1: 'Fitting 1',
      fitting2: 'Fitting 2',
      overallAssemblyLength: 'Overall Assembly Length',
      angleOfRotation: 'Angle of Rotation',
      customerPartNumber: 'Customer Part Number',
      notes: 'Notes',
      assemblyQty: 'Assembly Qty',
      testingTitle: 'Performed to industry standards',
      testingDescription: 'Pressure Test and Certify Assembly',
      accessories: 'Accessories',
    },
    nav: {
      profile: 'Profile',
      product: 'Product Type',
    },
    btn: {
      showFittingDetails: 'Show fitting details',
      showHoseDetails: 'Show hose details',
      hideFittingDetails: 'Hide fitting details',
      hideHoseDetails: 'Hide hose details',
      printBtn: 'Generate PDF',
      emailBtn: 'Email',
      reset: 'Start Over',
    },
    fitting: {
      // eslint-disable-next-line max-len
      hint: '',
    },
    footer: {
      text: 'Powered by Intelli.Build',
      link: 'https://www.intelli.build/',
    },
  },
} as const;

export type Type = typeof customization;
export type Texts = DeepKeys<typeof customization>;

export default customization;
